import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { map } from "rxjs/operators";
import {
  IBaseMetaDataSettings,
  IChoiceMetaDataSettings,
  ICirculationTemplate,
  IMetaData,
  INumberMetaDataSettings
} from "../../../interfaces/circulation";
import { LogClass, Logger } from "../../../logger";

@Component({
    selector: "circ-meta-basic",
    templateUrl: "./basic.component.html",
    styleUrls: ["./basic.component.scss"],
    standalone: false
})
@LogClass({
  source: "Templates/BasicMetaDataComponent"
})
export class BasicMetaDataComponent implements OnInit {
  public dropdownValues: DropdownItem[] = [
    {
      text: "circ.details.metadata.types.boolean",
      value: "boolean"
    },
    {
      text: "circ.details.metadata.types.text",
      value: "string"
    },
    {
      text: "circ.details.metadata.types.number",
      value: "number"
    },
    {
      text: "circ.details.metadata.types.choice",
      value: "choice"
    },
    {
      text: "circ.details.metadata.types.date",
      value: "date"
    }
  ];
  public selectedItem: DropdownItem = this.dropdownValues[0];
  public baseSettings: IBaseMetaDataSettings = {
    isReadonly: false,
    isVisibleInUi: true,
    isRequired: false
  };
  public numberSettings: INumberMetaDataSettings = {
    ...this.baseSettings,
    min: undefined,
    max: undefined,
    decimals: NaN
  };
  public choiceSettings: IChoiceMetaDataSettings = {
    ...this.baseSettings,
    choices: [],
    default: "",
    isMultiSelect: true
  };
  public loading = true;
  public isKeyExisting: boolean;
  private logger: Logger;

  public form = new FormGroup({
    key: new FormControl()
  });

  @Input()
  @Output()
  public template: ICirculationTemplate;

  @Output()
  public abort = new EventEmitter<void>();

  @Output()
  public save = new EventEmitter<ICirculationTemplate>();

  @Output()
  public error = new EventEmitter<string>();
  public metaData: IMetaData;

  constructor(private route: ActivatedRoute) {
    this.logger.debug("ctor");
  }

  ngOnInit(): void {
    const key$ = this.route.paramMap.pipe(map((params) => params.get("key")));

    key$.subscribe((key) => {
      this.metaData = this.template.payload.data?.find((m) => m.key == key);
    });

    this.setup();
    this.loading = false;
  }

  public keyValidator(): boolean {
    if (!this.form.value.key || this.form.value.key == "") {
      return;
    }
    // catch edit without namechange
    if (this.metaData) {
      if (this.metaData.key == this.form.value.key) {
        return true;
      }
    }

    this.isKeyExisting = false;
    const data = this.template.payload.data;
    if (data) {
      data.forEach((d) => {
        if (d.key == this.form.value.key) {
          this.isKeyExisting = true;
        }
      });
    }

    return !this.isKeyExisting;
  }

  public clickAbort(): void {
    this.abort.emit();
  }

  private setupForm() {
    this.form.setValue({
      key: this.metaData.key || ""
    });
  }

  private setup() {
    if (this.metaData) {
      this.setupDropdown();
      this.setupSettings();
      this.setupForm();
    }
  }

  private setupDropdown() {
    this.selectedItem = this.dropdownValues.find(
      (v) => v.value == this.metaData.valueType
    );
  }

  private setupSettings() {
    if (!this.metaData.settings) {
      return;
    }

    this.baseSettings = this.metaData.settings;
    if (this.metaData.valueType == "choice") {
      this.choiceSettings = this.metaData.settings as IChoiceMetaDataSettings;
    }
    if (this.metaData.valueType == "number") {
      this.numberSettings = this.metaData.settings as INumberMetaDataSettings;
    }
  }

  public clickSave(): void {
    this.form.markAllAsTouched();

    this.form.validator;
    let settingsType: IMetaData["settings"];

    if (!this.keyValidator()) {
      return;
    }

    switch (this.selectedItem.value) {
      case "choice":
        settingsType = this.choiceSettings;
        break;
      case "number":
        settingsType = this.numberSettings;
        break;
    }

    settingsType = {
      ...settingsType,
      ...this.baseSettings
    };

    const metadata: IMetaData = {
      key: this.form.value.key,
      value: "",
      displayValues: [],
      valueType: this.selectedItem.value,
      settings: settingsType
    };

    if (this.template.payload.data) {
      // edit or save metdata
      const dataIndex = this.template.payload.data?.indexOf(this.metaData);
      if (dataIndex != -1) {
        this.template.payload.data[dataIndex] = metadata;
      } else {
        this.template.payload.data.push(metadata);
      }
    } else {
      const data: IMetaData[] = [metadata];
      this.template.payload.data = data;
    }

    this.save.emit(this.template);
  }
}

export interface DropdownItem {
  text: string;
  value: IMetaData["valueType"];
}
